import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export interface CategorySliderItem {
  Id?: number | string;
  id?: number | string;
  Name?: string;
  name?: string;
  Title?: string;
  title?: string;
  ImageUrl?: string;
  imageUrl?: string;
  image?: string;
  ItemType?: string;
  itemType?: string;
  ItemId?: number | string | null;
  itemId?: number | string | null;
  RedirectUrl?: string | null;
  redirectUrl?: string | null;
  ConfigJson?: string | null;
  configJson?: string | null;
  DisplayOrder?: number;
  displayOrder?: number;
  IsActive?: boolean;
  isActive?: boolean;
  bgColor?: string;
  comingSoon?: boolean;
}

interface CategorySliderProps {
  items?: CategorySliderItem[];
  onCategoryClick?: (category: CategorySliderItem) => void;
}

export const CategorySlider: React.FC<CategorySliderProps> = ({
  items = [],
  onCategoryClick,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const safeItems = Array.isArray(items) ? items : [];

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollPosition = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  useEffect(() => {
    checkScrollPosition();
    const handleResize = () => checkScrollPosition();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [safeItems]);

  if (safeItems.length === 0) {
    return null;
  }

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.75;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const handleItemClick = (item: CategorySliderItem) => {
    const itemType = (item.ItemType ?? item.itemType ?? "").trim().toLowerCase();
    const redirectUrl = item.RedirectUrl ?? item.redirectUrl ?? "";

    if (itemType === "custom") {
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      return;
    }

    onCategoryClick?.(item);
  };

  return (
    <div className="relative w-full max-w-full group/slider py-2">
      {/* Left Scroll Arrow */}
      <button
        onClick={() => scroll("left")}
        disabled={!canScrollLeft}
        aria-label="Scroll Left"
        className={`absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-gray-800 transition-all duration-200 ${
          canScrollLeft
            ? "opacity-100 hover:bg-gray-50 hover:scale-110 active:scale-95 cursor-pointer"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronLeft size={20} />
      </button>

      {/* Slider / Carousel Track */}
      <div
        ref={scrollContainerRef}
        onScroll={checkScrollPosition}
        className="flex w-full max-w-full overflow-x-auto gap-3.5 sm:gap-4 md:gap-5 px-4 sm:px-6 md:px-8 py-2 snap-x snap-mandatory scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {safeItems.map((item, index) => {
          const itemId = item.Id ?? item.id ?? index;
          const itemType = (item.ItemType ?? item.itemType ?? "").trim().toLowerCase();
          const isCustom = itemType === "custom";
          const title = item.Title ?? item.title ?? item.Name ?? item.name ?? "Category";
          const imageUrl = item.ImageUrl ?? item.imageUrl ?? item.image ?? "";
          const isComingSoon = !isCustom && item.comingSoon === true;
          const backgroundClass = item.bgColor ?? "bg-slate-100";

          return (
            <div
              key={itemId}
              onClick={() => {
                if (isComingSoon) return;
                handleItemClick(item);
              }}
              className={`
                group
                relative
                flex-shrink-0
                w-[135px] sm:w-[160px] md:w-[185px] lg:w-[200px]
                aspect-square
                rounded-2xl
                snap-start
                ${backgroundClass}
                flex
                items-end
                justify-center
                overflow-hidden
                transition-all
                duration-300
                ease-out
                ${
                  isComingSoon
                    ? "cursor-not-allowed"
                    : "cursor-pointer hover:shadow-lg hover:-translate-y-1"
                }
              `}
            >
              {isCustom ? (
                <>
                  <div className="absolute inset-0 bg-white/10 transition duration-300 group-hover:bg-white/20" />
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={title}
                      className="absolute inset-0 w-full h-full object-contain p-3 transition-all duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">
                      No Image
                    </div>
                  )}

                  <div className="absolute left-0 right-0 bottom-0 z-10 px-2.5 py-2.5 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
                    <p className="text-white text-xs sm:text-sm font-semibold text-center leading-tight drop-shadow-md truncate">
                      {title}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {!isComingSoon && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-300 bg-white/20 blur-xl" />
                  )}

                  <span className="absolute top-3 left-3 text-base sm:text-lg md:text-xl font-bold text-black/10 leading-none transition-all duration-300 group-hover:scale-105 truncate max-w-[80%]">
                    {title}
                  </span>

                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={title}
                      className={`
                        h-[70%]
                        object-contain
                        transition-all
                        duration-500
                        ${
                          !isComingSoon
                            ? "group-hover:scale-110 group-hover:-rotate-2"
                            : "filter blur-[0.5px]"
                        }
                      `}
                    />
                  ) : (
                    <div className="h-[70%] w-full flex items-center justify-center text-xs text-slate-400">
                      No Image
                    </div>
                  )}

                  {isComingSoon && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-[0.5px] flex items-center justify-center p-2 text-center">
                      <span className="text-white font-bold text-[11px] sm:text-xs tracking-wider bg-black/30 px-2 py-1 rounded-lg border border-white/10">
                        Coming Soon
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Right Scroll Arrow */}
      <button
        onClick={() => scroll("right")}
        disabled={!canScrollRight}
        aria-label="Scroll Right"
        className={`absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-gray-800 transition-all duration-200 ${
          canScrollRight
            ? "opacity-100 hover:bg-gray-50 hover:scale-110 active:scale-95 cursor-pointer"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

export default CategorySlider;